import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { message, Spin } from 'antd';
import {
  Copy,
  ExternalLink,
  MessageSquare,
  Info,
  User,
  FileText,
  Package,
  ChevronRight,
} from 'lucide-react';
import useLanguageStore from '@/stores/useLanguageStore';
import {
  useFetchTicketById,
  useFetchTicketComments,
  useFetchTicketFormFields,
  useUpdateTicket,
} from '@/hooksApi';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import {
  formatDateByDayjs,
  getFormData,
  getIssueField,
  getIssueOptionsFromForm,
  getTicketCreatorKind,
  getTicketEntityAndPageRoute,
  shouldShowDescriptionSeparately,
} from '@/utils';
import CommentForm from '../../components/TicketComment/CommentForm';
import { useQueryClient } from '@tanstack/react-query';
import { entityFieldList } from '@/constants';
import { ROUTE } from '@/config';
import { getStatusTranslationKeyFromRaw, TicketStatusEnum } from '@/utils/ticketStatus';

/* ─── Status HSL colors (must match list.tsx / redesign) ─── */
import { TICKET_STATUS_HSL } from './list';

/* ─── Section Header (matches all detail pages) ─── */
function SectionHeader({ icon: Icon, title }: { icon: typeof FileText; title: string }) {
  return (
    <div className='flex items-center gap-2 mb-4'>
      <Icon className='h-4 w-4 text-sd-muted-foreground' />
      <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
        {title}
      </span>
    </div>
  );
}

const SingleTicket = () => {
  const [isClosingTicket, setIsClosingTicket] = useState(false);

  const { t } = useTranslation();
  const { id } = useParams();
  const { language } = useLanguageStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();

  const {
    data: ticketData,
    isLoading: isLoadingTicket,
    isError: isTicketError,
  } = useFetchTicketById(id as string, language, {
    enabled: !!id,
    retry: false,
  });

  // Navigate away if ticket not found
  useEffect(() => {
    if (!isLoadingTicket && isTicketError) {
      message.error(t('ticket_not_found'));
      navigate(ROUTE.TICKET_CENTER);
    }
  }, [isLoadingTicket, isTicketError]);
  const { mutate: updateTicket } = useUpdateTicket();
  const { data: comments, isLoading: isLoadingComments } = useFetchTicketComments(
    id as string,
    !!ticketData,
  );
  const { data: formData } = useFetchTicketFormFields(
    language,
    !!ticketData && hasPermission(PermissionName.DASHBOARD),
  );

  const rawStatus = ticketData?.object.status?.toLowerCase() ?? 'open';
  const statusHsl = TICKET_STATUS_HSL[rawStatus] ?? {
    bg: 'hsla(0,0%,50%,0.10)',
    text: 'hsl(0,0%,40%)',
  };

  const external_id =
    ticketData?.object?.external_id ??
    ticketData?.object?.fields?.find((field) => entityFieldList.includes(field.id) && !!field.value)
      ?.value ??
    undefined;

  const entityData = getTicketEntityAndPageRoute(
    external_id ?? '',
    ticketData?.object.entity_uid ?? undefined,
  );

  const isSolved = ['resolve', 'resolved', 'solved', 'closed'].includes(rawStatus);
  // BOXY-249 / R7 — a solved/resolved ticket must keep its composer: the
  // merchant's comment is what reopens it (backend flips status on comment).
  // Only `closed` locks — the ticketing service makes closed tickets
  // immutable, a reply there would fail. Hiding the form on solved made the
  // list's Reopen chip a dead end, same defect the mobile app had.
  const canComment = rawStatus !== 'closed';

  const resolveTicket = () => {
    setIsClosingTicket(true);
    if (id)
      updateTicket(
        { status: TicketStatusEnum.SOLVED, id },
        {
          onSuccess: () => {
            message.success(t('operation_completed_successfully'));
            queryClient.invalidateQueries({ queryKey: ['ticket', id] });
            setIsClosingTicket(false);
          },
          onError: () => setIsClosingTicket(false),
        },
      );
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(String(ticketData?.object.id ?? id));
    message.success(String(t('copied')));
  };

  if (isLoadingTicket) {
    return (
      <div className='flex items-center justify-center min-h-[300px]'>
        <div className='h-8 w-8 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
      </div>
    );
  }

  if (!ticketData?.object) return null;
  const ticket = ticketData.object;

  const stripHtml = (s: string) =>
    (s || '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  // BOXY-247 — some writers store the body WITH markup (`<p>…</p>`), which
  // rendered literally here. Flatten markup to plain text for DISPLAY — never
  // render it: SEC-1 stands, an HTML sink here is account takeover. Unlike
  // stripHtml above (comparison-only, collapses all whitespace), this keeps
  // paragraph shape: block breaks become newlines for whitespace-pre-wrap.
  // A lone '<' with no closing '>' ("a < b") is untouched by the tag regex.
  const flattenMarkup = (str: string) =>
    (str || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  const showDescriptionBlock = shouldShowDescriptionSeparately(ticket);
  const descPlain = stripHtml(ticket.description ?? '');
  const allPublicComments = (comments?.object?.comments ?? []).filter((c) => c.public);
  // The auto-posted first comment that mirrors the description text — upstream
  // emits it on creation; the Description section already shows that text.
  const isDescriptionMirror = (c: (typeof allPublicComments)[number], idx: number) => {
    if (!showDescriptionBlock || idx !== 0) return false;
    const body = stripHtml(c.body || '');
    return !!body && !!descPlain && body === descPlain;
  };
  const publicComments = allPublicComments.filter((c, idx) => !isDescriptionMirror(c, idx));
  // QA bug: files uploaded while CREATING the ticket (admin-on-behalf flow)
  // live on that mirror comment — hiding it must not hide its attachments,
  // so they are re-homed onto the Description section instead.
  const descriptionAttachments = allPublicComments
    .filter((c, idx) => isDescriptionMirror(c, idx))
    .flatMap((c) => c.attachments ?? []);

  return (
    <div className='relative flex flex-col h-full'>
      {/* ─── Sub-header: Subject + date + status pill ─── */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <h2 className='text-xl font-bold tracking-tight text-sd-foreground m-0'>
            {ticket.subject ?? ticket.title ?? getIssueField(ticket)?.name ?? '—'}
          </h2>
          <p className='text-[12px] text-sd-muted-foreground mt-1 m-0'>
            {formatDateByDayjs(ticket.created_at, 'D MMMM YYYY')}
          </p>
          {getTicketCreatorKind(ticket) === 'admin-on-behalf' && (
            <span
              data-testid='ticket-created-by-admin-badge'
              className='inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium'
              style={{ backgroundColor: 'hsla(210,65%,55%,0.12)', color: 'hsl(210,60%,42%)' }}
            >
              <User className='h-3 w-3' />
              {String(
                t('ticket_created_by_admin', {
                  defaultValue: 'Created on your behalf by Boxy support',
                }),
              )}
            </span>
          )}
        </div>
        <span
          className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap shrink-0'
          style={{ backgroundColor: statusHsl.bg, color: statusHsl.text }}
        >
          {String(t(getStatusTranslationKeyFromRaw(rawStatus)))}
        </span>
      </div>

      {/* ─── 2-Column Grid ─── */}
      <div className='grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start pb-8'>
        {/* LEFT COLUMN */}
        <div className='flex flex-col gap-5'>
          {/* Description — only shown when title and description are genuinely distinct
              (admin-on-behalf tickets, where Boxy support typed a real description). */}
          {showDescriptionBlock && (
            <section
              data-testid='ticket-description-section'
              className='rounded-2xl border border-sd-border bg-sd-card overflow-hidden p-5'
            >
              <SectionHeader
                icon={FileText}
                title={String(t('ticket_description_label', { defaultValue: 'Description' }))}
              />
              {/*
                SEC-1 — rendered as a TEXT CHILD, never as HTML.

                `ticket.description` and `comment.body` are plain text all the
                way down: CommentRequest validates a bare string,
                CommentTransformer maps Zendesk's plain `body` (not
                `html_body`) into `content` verbatim, and TicketComment.tsx —
                the other renderer of this same field — has always shown it as
                text. `whitespace-pre-wrap` is what makes the newlines work;
                nothing here ever needed markup.

                Do not reintroduce dangerouslySetInnerHTML. A sub-user can
                write a comment that renders in the account owner's session
                (owners bypass permission checks via Gate::before), and the API
                token lives in localStorage — so an HTML sink here is account
                takeover inside one tenant, not a cosmetic issue.
              */}
              <div className='whitespace-pre-wrap text-[14px] leading-relaxed text-sd-foreground m-0'>
                {flattenMarkup(ticket.description ?? '')}
              </div>
              {descriptionAttachments.length > 0 && (
                <div className='flex flex-col gap-1.5 mt-3'>
                  {descriptionAttachments.map((file) => (
                    <a
                      key={file.id}
                      href={file.mapped_content_url || file.content_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center gap-2 rounded-xl px-3 py-2 no-underline bg-sd-muted text-sd-foreground'
                    >
                      <FileText className='w-4 h-4 shrink-0 text-sd-muted-foreground' />
                      <div className='flex flex-col min-w-0'>
                        <span className='text-[12px] font-medium truncate'>
                          {file.file_name ?? 'Attachment'}
                        </span>
                        {file.size != null && (
                          <span className='text-[11px] text-sd-muted-foreground'>
                            {file.size > 1024 * 1024
                              ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                              : `${Math.round(file.size / 1024)} KB`}
                          </span>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Conversation — chat-style bubbles */}
          <section className='rounded-2xl border border-sd-border bg-sd-card overflow-hidden p-5'>
            <SectionHeader
              icon={MessageSquare}
              title={String(t('conversation', { defaultValue: 'Conversation' }))}
            />
            {isLoadingComments ? (
              <div className='flex justify-center py-10'>
                <div className='h-6 w-6 animate-spin rounded-full border-2 border-sd-primary border-t-transparent' />
              </div>
            ) : (
              (() => {
                return (
                  <div className='space-y-5'>
                    {publicComments.map((comment) => {
                      const isSupport = comment.author?.role === 'admin';
                      const isCustomer = !isSupport;
                      return (
                        <div
                          key={comment.id}
                          className={`flex flex-col gap-1.5 ${isCustomer ? 'items-end' : 'items-start'}`}
                        >
                          <span className='text-[12px] font-semibold text-sd-muted-foreground px-1'>
                            {isCustomer
                              ? String(t('you', { defaultValue: 'You' }))
                              : (comment.author?.name ??
                                String(t('support', { defaultValue: 'Support' })))}
                          </span>
                          <div
                            className={`rounded-2xl px-4 py-3 text-[14px] leading-relaxed max-w-[88%] ${
                              isCustomer
                                ? 'bg-sd-primary text-[rgb(23,23,23)]'
                                : 'bg-sd-muted text-sd-foreground'
                            }`}
                          >
                            {/* SEC-1 — text child, never HTML. See the note on
                              the description above. */}
                            <div className='whitespace-pre-wrap m-0'>
                              {flattenMarkup(comment.body)}
                            </div>
                            {comment.attachments?.length > 0 && (
                              <div className='flex flex-col gap-1.5 mt-2'>
                                {comment.attachments.map((file) => (
                                  <a
                                    key={file.id}
                                    href={file.mapped_content_url || file.content_url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={`flex items-center gap-2 rounded-xl px-3 py-2 no-underline ${
                                      isCustomer
                                        ? 'bg-black/10 text-[rgb(23,23,23)]'
                                        : 'bg-sd-card border border-sd-border text-sd-foreground'
                                    }`}
                                  >
                                    <FileText className='w-4 h-4 shrink-0 text-sd-muted-foreground' />
                                    <div className='flex flex-col min-w-0'>
                                      <span className='text-[12px] font-medium truncate'>
                                        {file.file_name ?? 'Attachment'}
                                      </span>
                                      {file.size != null && (
                                        <span className='text-[11px] text-sd-muted-foreground'>
                                          {file.size > 1024 * 1024
                                            ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                            : `${Math.round(file.size / 1024)} KB`}
                                        </span>
                                      )}
                                    </div>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className='text-[11px] text-sd-muted-foreground px-1'>
                            {formatDateByDayjs(comment.created_at, 'DD MMM YYYY, HH:mm')}
                          </span>
                        </div>
                      );
                    })}
                    {publicComments.length === 0 && (
                      <p className='text-[13px] text-sd-muted-foreground text-center py-4'>
                        {String(t('no_messages', { defaultValue: 'No messages yet' }))}
                      </p>
                    )}
                  </div>
                );
              })()
            )}

            {/* Composer — border-t extends full card width (redesign: px-5 py-4 with -mx-5) */}
            {hasPermission(PermissionName.ADD_COMMENT) && canComment && (
              <div className='border-t border-sd-border -mx-5 px-5 py-4 mt-5'>
                <CommentForm ticketId={id as string} disabled={!canComment} />
              </div>
            )}
          </section>
        </div>

        {/* RIGHT COLUMN (sticky) */}
        <div className='flex flex-col gap-5 xl:sticky xl:top-0'>
          {/* Ticket Info card */}
          <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
            <SectionHeader
              icon={Info}
              title={String(t('ticket_info', { defaultValue: 'Ticket Info' }))}
            />
            <div className='flex flex-col divide-y divide-sd-border'>
              <div className='flex items-center justify-between py-2'>
                <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                  {String(t('type'))}
                </span>
                <span className='text-sm font-medium text-end'>
                  {getIssueField(ticket)?.name ??
                    (ticket.ticket_form_id && formData?.object
                      ? formData.object.find((f: any) => f.id === ticket.ticket_form_id)?.name
                      : undefined) ??
                    '—'}
                </span>
              </div>
              <div className='flex items-center justify-between py-2'>
                <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                  {String(t('ticket_id', { defaultValue: 'Ticket ID' }))}
                </span>
                <span className='text-sm font-medium'>{ticket.id ?? '—'}</span>
              </div>
              {external_id &&
                entityData?.singlePageUrl &&
                (() => {
                  const prefix = external_id.split('-')[0]?.toUpperCase();
                  const entityLabel =
                    prefix === 'BXO'
                      ? t('order')
                      : prefix === 'BXPU'
                        ? t('pickup', { defaultValue: 'Pickup' })
                        : prefix === 'BXRTO'
                          ? t('return', { defaultValue: 'Return' })
                          : prefix === 'BXRE'
                            ? t('remittance')
                            : t('entity');
                  return (
                    <div className='flex items-center justify-between py-2'>
                      <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                        {entityLabel}
                      </span>
                      <button
                        onClick={() => window.open(entityData.singlePageUrl!, '_blank')}
                        className='flex items-center gap-1.5 text-sm font-medium hover:text-sd-primary transition-colors bg-transparent border-0 cursor-pointer p-0'
                      >
                        <Package className='w-3.5 h-3.5 text-sd-muted-foreground' />
                        <span style={{ fontFamily: 'monospace' }}>{external_id}</span>
                        <ChevronRight className='w-3.5 h-3.5 text-sd-muted-foreground rtl:rotate-180' />
                      </button>
                    </div>
                  );
                })()}
            </div>
          </section>

          {/* Details card */}
          <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
            <SectionHeader icon={User} title={String(t('details', { defaultValue: 'Details' }))} />
            <div className='flex flex-col divide-y divide-sd-border'>
              <div className='flex items-center justify-between py-2'>
                <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                  {String(t('assigned_to', { defaultValue: 'Assigned to' }))}
                </span>
                <span className='text-sm font-medium text-end max-w-[55%]'>
                  {ticket.assignee?.name ?? ticket.group?.name ?? '—'}
                </span>
              </div>
              <div className='flex items-center justify-between py-2'>
                <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                  {String(t('created'))}
                </span>
                <span className='text-sm font-medium'>
                  {formatDateByDayjs(ticket.created_at, 'DD MMM YYYY, HH:mm')}
                </span>
              </div>
              <div className='flex items-center justify-between py-2'>
                <span className='text-sm text-sd-muted-foreground shrink-0 ltr:mr-4 rtl:ml-4'>
                  {String(t('last_activity', { defaultValue: 'Last activity' }))}
                </span>
                <span className='text-sm font-medium'>
                  {formatDateByDayjs(ticket.updated_at, 'DD MMM YYYY, HH:mm')}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SingleTicket;
